import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useIntl } from "react-intl";
import { useSelector } from "react-redux";
import {
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { FaPlus } from "react-icons/fa";

import {
  ActionButton,
  ActionsCell,
  Container,
  CustomSearch,
  HeaderContent,
  NewReasonButton,
} from "./styles";
import AnimatedMainContainer from "../../../components/AnimatedMainContainer";
import ReasonModal from "../components/ReasonModal";
import {
  DEFAULT_REJECTION_REASONS_PAGINATION,
  deleteRejectionReasonService,
  getRejectionReasonsService,
  postRejectionReasonService,
  putRejectionReasonService,
} from "../../../service/uploadFlow.service";
import { useToast } from "../../../hooks/toast";
import Table from "../../../components/Table";

const MODAL_INITIAL_STATE = Object.freeze({
  open: false,
  mode: ReasonModal.MODES.CREATE,
  record: null,
});

const SEARCH_DEBOUNCE_MS = 400;
const REJECTION_REASONS_PAGE_KEY = "rejectionReasonsPage";
const REASON_IN_USE_ERROR_CODE = -46023;

const mapApiReasonToRow = (item) => ({
  key: String(item?.id),
  id: item?.id,
  reason: item?.description ?? "",
  createdAt: item?.createdAt ?? null,
  updatedAt: item?.updatedAt ?? null,
});

const getApiErrorId = (error) => `api.error.${error?.response?.data?.code}`;

const ReasonsDisapprovalDocuments = () => {
  const intl = useIntl();
  const { token } = useSelector((state) => state.auth);
  const { addToast } = useToast();

  const [modalState, setModalState] = useState(MODAL_INITIAL_STATE);
  const [searchValue, setSearchValue] = useState("");
  const [loadingModal, setLoadingModal] = useState(false);

  const [loadingTable, setLoadingTable] = useState(false);
  const [reasons, setReasons] = useState([]);

  const [page, setPage] = useState(DEFAULT_REJECTION_REASONS_PAGINATION.page);
  const [size, setSize] = useState(DEFAULT_REJECTION_REASONS_PAGINATION.size);
  const [total, setTotal] = useState(0);

  // Refs para evitar closure com valores antigos (resolve "voltou pra 10 do nada")
  const pageRef = useRef(page);
  const sizeRef = useRef(size);
  const searchRef = useRef(searchValue);

  useEffect(() => {
    pageRef.current = page;
  }, [page]);

  useEffect(() => {
    sizeRef.current = size;
  }, [size]);

  useEffect(() => {
    searchRef.current = searchValue;
  }, [searchValue]);

  const openModal = useCallback(({ mode, record = null }) => {
    setModalState({ open: true, mode, record });
  }, []);

  const closeModal = useCallback(() => {
    setModalState(MODAL_INITIAL_STATE);
  }, []);

  const handleSearchChange = useCallback((event) => {
    const value = event?.target?.value ?? "";
    setSearchValue(value);
  }, []);

  const handleClickNew = useCallback(() => {
    openModal({ mode: ReasonModal.MODES.CREATE });
  }, [openModal]);

  const handleView = useCallback(
    (record) => openModal({ mode: ReasonModal.MODES.VIEW, record }),
    [openModal]
  );

  const handleEdit = useCallback(
    (record) => openModal({ mode: ReasonModal.MODES.EDIT, record }),
    [openModal]
  );

  const handleDelete = useCallback(
    (record) => openModal({ mode: ReasonModal.MODES.DELETE, record }),
    [openModal]
  );

  const toNumberOrFallback = useCallback((value, fallback) => {
    const asNumber = Number(value);
    return Number.isNaN(asNumber) ? fallback : asNumber;
  }, []);

  const getReasonsPageFromResponse = useCallback(
    (data) => data?.[REJECTION_REASONS_PAGE_KEY] ?? {},
    []
  );

  const getReasonsFromResponse = useCallback(
    (data) => {
      const pageData = getReasonsPageFromResponse(data);
      return Array.isArray(pageData?.content) ? pageData.content : [];
    },
    [getReasonsPageFromResponse]
  );

  const getPageFromResponse = useCallback(
    (data, fallbackPage) => {
      const pageData = getReasonsPageFromResponse(data);
      return toNumberOrFallback(pageData?.pageable?.pageNumber, fallbackPage);
    },
    [getReasonsPageFromResponse, toNumberOrFallback]
  );

  const getSizeFromResponse = useCallback(
    (data, fallbackSize) => {
      const pageData = getReasonsPageFromResponse(data);
      return toNumberOrFallback(pageData?.pageable?.pageSize, fallbackSize);
    },
    [getReasonsPageFromResponse, toNumberOrFallback]
  );

  const getTotalFromResponse = useCallback(
    (data, fallbackTotal) => {
      const pageData = getReasonsPageFromResponse(data);

      // No JSON enviado não veio totalElements.
      // Se backend incluir no futuro, esse código passa a usar automaticamente.
      return toNumberOrFallback(pageData?.totalElements, fallbackTotal);
    },
    [getReasonsPageFromResponse, toNumberOrFallback]
  );

  const fetchReasons = useCallback(
    async ({ nextPage, nextSize, searchTerm } = {}) => {
      try {
        setLoadingTable(true);

        const resolvedPage =
          nextPage ??
          pageRef.current ??
          DEFAULT_REJECTION_REASONS_PAGINATION.page;

        const resolvedSize =
          nextSize ??
          sizeRef.current ??
          DEFAULT_REJECTION_REASONS_PAGINATION.size;

        const resolvedSearch = String(
          searchTerm ?? searchRef.current ?? ""
        ).trim();

        const { data } = await getRejectionReasonsService(
          {
            ...DEFAULT_REJECTION_REASONS_PAGINATION,
            page: resolvedPage,
            size: resolvedSize,
            search: resolvedSearch,
          },
          token
        );

        const items = getReasonsFromResponse(data);

        setReasons(items.map(mapApiReasonToRow));
        setPage(getPageFromResponse(data, resolvedPage));
        setSize(getSizeFromResponse(data, resolvedSize));
        setTotal(getTotalFromResponse(data, items.length));
      } catch (error) {
        addToast({
          type: "error",
          title: intl.formatMessage({ id: "error" }),
          description: intl.formatMessage({ id: getApiErrorId(error) }),
        });
      } finally {
        setLoadingTable(false);
      }
    },
    [
      addToast,
      getPageFromResponse,
      getReasonsFromResponse,
      getSizeFromResponse,
      getTotalFromResponse,
      intl,
      token,
    ]
  );

  // Carga inicial: executa uma vez (evita re-disparo)
  useEffect(() => {
    fetchReasons({
      nextPage: DEFAULT_REJECTION_REASONS_PAGINATION.page,
      nextSize: DEFAULT_REJECTION_REASONS_PAGINATION.size,
      searchTerm: "",
    });
    // intencionalmente vazio para rodar apenas no mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Busca em tempo real (debounce) — dispara só quando muda o termo
  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      fetchReasons({
        nextPage: DEFAULT_REJECTION_REASONS_PAGINATION.page,
        nextSize: sizeRef.current,
        searchTerm: searchValue,
      });
    }, SEARCH_DEBOUNCE_MS);

    return () => window.clearTimeout(timeoutId);
  }, [fetchReasons, searchValue]);

  const handleCreate = useCallback(
    async (payload) => {
      try {
        setLoadingModal(true);

        await postRejectionReasonService(
          { description: payload.reason },
          token
        );

        addToast({
          type: "success",
          title: intl.formatMessage({ id: "success" }),
          description: intl.formatMessage({
            id: "reasonsDisapprovalDocuments.feedback.create.success",
          }),
        });

        closeModal();

        await fetchReasons({
          nextPage: pageRef.current,
          nextSize: sizeRef.current,
          searchTerm: searchRef.current,
        });
      } catch (error) {
        addToast({
          type: "error",
          title: intl.formatMessage({ id: "error" }),
          description: intl.formatMessage({ id: getApiErrorId(error) }),
        });
      } finally {
        setLoadingModal(false);
      }
    },
    [addToast, closeModal, fetchReasons, intl, token]
  );

  const handleUpdate = useCallback(
    async ({ id, values }) => {
      try {
        setLoadingModal(true);

        await putRejectionReasonService(
          id,
          { description: values.reason },
          token
        );

        addToast({
          type: "success",
          title: intl.formatMessage({ id: "success" }),
          description: intl.formatMessage({
            id: "reasonsDisapprovalDocuments.feedback.update.success",
          }),
        });

        closeModal();

        await fetchReasons({
          nextPage: pageRef.current,
          nextSize: sizeRef.current,
          searchTerm: searchRef.current,
        });
      } catch (error) {
        addToast({
          type: "error",
          title: intl.formatMessage({ id: "error" }),
          description: intl.formatMessage({ id: getApiErrorId(error) }),
        });
      } finally {
        setLoadingModal(false);
      }
    },
    [addToast, closeModal, fetchReasons, intl, token]
  );

  const handleDeleteReason = useCallback(
    async ({ id }, { confirm = false } = {}) => {
      try {
        setLoadingModal(true);

        await deleteRejectionReasonService(id, token, { confirm });

        addToast({
          type: "success",
          title: intl.formatMessage({ id: "success" }),
          description: intl.formatMessage({
            id: confirm
              ? "reasonsDisapprovalDocuments.feedback.inactivate.success"
              : "reasonsDisapprovalDocuments.feedback.delete.success",
          }),
        });

        closeModal();

        await fetchReasons({
          nextPage: pageRef.current,
          nextSize: sizeRef.current,
          searchTerm: searchRef.current,
        });
      } catch (error) {
        const apiCode = error?.response?.data?.code;

        // Motivo já utilizado: backend não permite exclusão, apenas inativação.
        if (!confirm && apiCode === REASON_IN_USE_ERROR_CODE) {
          openModal({
            mode: ReasonModal.MODES.INACTIVATE,
            record: { id },
          });
          return;
        }

        addToast({
          type: "error",
          title: intl.formatMessage({ id: "error" }),
          description: intl.formatMessage({ id: getApiErrorId(error) }),
        });
      } finally {
        setLoadingModal(false);
      }
    },
    [addToast, closeModal, fetchReasons, intl, openModal, token]
  );

  const columns = useMemo(
    () => [
      {
        name: intl.formatMessage({ id: "reason" }),
        selector: (row) => row.reason,
        sortable: false,
        grow: 2,
      },
      {
        name: intl.formatMessage({ id: "actions" }),
        cell: (row) => (
          <ActionsCell>
            <ActionButton
              icon={<EyeOutlined />}
              onClick={() => handleView(row)}
              aria-label={intl.formatMessage({ id: "visualize" })}
            >
              {intl.formatMessage({ id: "visualize" })}
            </ActionButton>

            <ActionButton
              icon={<EditOutlined />}
              onClick={() => handleEdit(row)}
              aria-label={intl.formatMessage({ id: "edit" })}
            >
              {intl.formatMessage({ id: "edit" })}
            </ActionButton>

            <ActionButton
              danger
              icon={<DeleteOutlined />}
              style={{
                color: "rgba(153, 27, 27, 1)",
                background: "rgba(254, 202, 202, 1)",
              }}
              onClick={() => handleDelete(row)}
              aria-label={intl.formatMessage({ id: "delete" })}
            >
              {intl.formatMessage({ id: "delete" })}
            </ActionButton>
          </ActionsCell>
        ),
        right: true,
        ignoreRowClick: true,
        allowOverflow: true,
        button: true,
      },
    ],
    [handleDelete, handleEdit, handleView, intl]
  );

  return (
    <AnimatedMainContainer
      breadcrumb={intl.formatMessage({
        id: "sidebar.reasons_disapproval-documents",
      })}
    >
      <Container>
        <HeaderContent>
          <CustomSearch
            prefix={
              <SearchOutlined
                style={{
                  color: "rgba(0, 0, 0, 0.25)",
                }}
              />
            }
            placeholder={intl.formatMessage({ id: "search_reason" })}
            allowClear
            size="large"
            value={searchValue}
            onChange={handleSearchChange}
          />

          <NewReasonButton type="button" onClick={handleClickNew}>
            <FaPlus />
            {intl.formatMessage({ id: "new_reason" })}
          </NewReasonButton>
        </HeaderContent>

        <Table
          columns={columns}
          data={reasons}
          progressPending={loadingTable}
          hasPagination
          paginationServer
          paginationTotalRows={total}
          paginationPerPage={size}
          paginationDefaultPage={page + 1}
          onChangePage={(nextPageNumber) => {
            const nextPage = Math.max(0, Number(nextPageNumber) - 1);

            fetchReasons({
              nextPage,
              nextSize: sizeRef.current,
              searchTerm: searchRef.current,
            });
          }}
          onChangeRowsPerPage={(nextSize, nextPageNumber) => {
            const resolvedSize = Number(nextSize);
            const resolvedPage = Math.max(0, Number(nextPageNumber ?? 1) - 1);

            fetchReasons({
              nextPage: resolvedPage,
              nextSize: resolvedSize,
              searchTerm: searchRef.current,
            });
          }}
        />

        <ReasonModal
          open={modalState.open}
          mode={modalState.mode}
          initialValues={modalState.record}
          loading={loadingModal}
          onClose={closeModal}
          onCreate={handleCreate}
          onUpdate={handleUpdate}
          onDelete={handleDeleteReason}
        />
      </Container>
    </AnimatedMainContainer>
  );
};

export default ReasonsDisapprovalDocuments;
